import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Check, X } from 'lucide-react';
import { resetPassword } from './api/resetPassword';

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [formData, setFormData] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);
  const [errors, setErrors] = useState({});
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [showPasswordRequirements, setShowPasswordRequirements] = useState(false);

  const validatePassword = (password) => {
    const errors = [];
    
    if (password.length < 8) {
      errors.push('Minimum 8 characters long');
    }
    
    if (!/[A-Z]/.test(password)) {
      errors.push('Include uppercase letter');
    }
    
    if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
      errors.push('Include special character');
    }
    
    return errors;
  };

  const calculatePasswordStrength = (password) => {
    let strength = 0;
    if (password.length >= 8) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[a-z]/.test(password)) strength++;
    if (/[0-9]/.test(password)) strength++;
    if (/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) strength++;
    return strength;
  };

  const getPasswordStrengthText = (strength) => {
    if (strength <= 2) return { text: 'Weak', color: 'danger' };
    if (strength <= 3) return { text: 'Fair', color: 'warning' };
    if (strength <= 4) return { text: 'Good', color: 'info' };
    return { text: 'Strong', color: 'success' };
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Real-time validation for new password
    if (name === 'newPassword') {
      const passwordErrors = validatePassword(value);
      const strength = calculatePasswordStrength(value);
      setPasswordStrength(strength);
      
      if (passwordErrors.length > 0) {
        setErrors(prev => ({
          ...prev,
          newPassword: passwordErrors[0]
        }));
      } else {
        setErrors(prev => ({
          ...prev,
          newPassword: ''
        }));
      }
      
      // Show requirements when user starts typing
      if (value.length > 0) {
        setShowPasswordRequirements(true);
      }
    }
    
    // Real-time validation for confirm password
    if (name === 'confirmPassword') {
      if (value !== formData.newPassword) {
        setErrors(prev => ({
          ...prev,
          confirmPassword: 'Passwords do not match'
        }));
      } else if (value === formData.newPassword && value.length > 0) {
        setErrors(prev => ({
          ...prev,
          confirmPassword: ''
        }));
      }
    }
    
    // Clear errors when user starts typing
    if (errors[name] && value.length === 0) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Validate new password
    if (!formData.newPassword) {
      newErrors.newPassword = 'Please enter a new password';
    } else {
      const passwordErrors = validatePassword(formData.newPassword);
      if (passwordErrors.length > 0) {
        newErrors.newPassword = passwordErrors[0]; // Show first error
      }
    }
    
    // Validate confirm password
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.confirmPassword !== formData.newPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    if (!token) {
      setMessage('Invalid reset token');
      setIsSuccess(false);
      return;
    }

    setIsLoading(true);
    setMessage('');

    try {
      await resetPassword(token, formData.newPassword);
      setMessage('Password has been reset successfully! You can now sign in with your new password.');
      setIsSuccess(true);
      setFormData({ newPassword: '', confirmPassword: '' });
    } catch (error) {
      setMessage(error.message || 'Failed to reset password. Please try again.');
      setIsSuccess(false);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-vh-100 d-flex align-items-center justify-content-center" style={{ backgroundColor: '#f8f9fa', overflow: 'hidden' }}>
      <div className="container-fluid px-3">
        <div className="row justify-content-center">
          <div className="col-12 col-sm-10 col-md-8 col-lg-6 col-xl-4">
            {/* Logo */}
            <div className="text-center mb-4">
              <img src="/static/logo.png" width={160} alt="Logo" />
            </div>
            
            {/* Form Card */}
            <div className="card shadow-sm" style={{ borderRadius: '6px', maxWidth: '100%' }}>
              <div className="card-body p-4">
                <h2 className="card-title mb-2 fw-bold">Reset Password</h2>
                <p className="text-muted mb-2">
                  Reset your password below
                </p>

                {/* Password Requirements */}
                <div className="mb-4">
                  <p className="small text-muted mb-2">Ensure that these requirements are met:</p>
                  <ul className="small text-muted mb-0">
                    <li className={formData.newPassword.length >= 8 ? 'text-success' : 'text-danger'}>
                      <span className="d-flex align-items-center">
                        <span className="me-2">Minimum 8 characters long</span>
                        {formData.newPassword.length >= 8 ? (
                          <Check size={16} className="text-success" />
                        ) : (
                          <X size={16} className="text-danger" />
                        )}
                      </span>
                    </li>
                    <li className={/[A-Z]/.test(formData.newPassword) ? 'text-success' : 'text-danger'}>
                      <span className="d-flex align-items-center">
                        <span className="me-2">Include uppercase letter</span>
                        {/[A-Z]/.test(formData.newPassword) ? (
                          <Check size={16} className="text-success" />
                        ) : (
                          <X size={16} className="text-danger" />
                        )}
                      </span>
                    </li>
                    <li className={/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(formData.newPassword) ? 'text-success' : 'text-danger'}>
                      <span className="d-flex align-items-center">
                        <span className="me-2">Include special character</span>
                        {/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(formData.newPassword) ? (
                          <Check size={16} className="text-success" />
                        ) : (
                          <X size={16} className="text-danger" />
                        )}
                      </span>
                    </li>
                  </ul>
                </div>

                {message && (
                  <div className={`alert ${isSuccess ? 'alert-success' : 'alert-danger'} mb-3`}>
                    {message}
                  </div>
                )}

                <form onSubmit={handleSubmit}>
                  {/* New Password Field */}
                  <div className="mb-3">
                    <label htmlFor="newPassword" className="form-label fw-semibold">New Password</label>
                    <div className="input-group">
                      <input
                        type={showNewPassword ? 'text' : 'password'}
                        className={`form-control ${errors.newPassword ? 'is-invalid' : formData.newPassword && !errors.newPassword ? 'is-valid' : ''}`}
                        id="newPassword"
                        name="newPassword"
                        placeholder="Enter new password"
                        value={formData.newPassword}
                        onChange={handleChange}
                        disabled={isLoading}
                      />
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        disabled={isLoading}
                        style={{ borderRadius: '0 6px 6px 0' }}
                      >
                        {showNewPassword ? (
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                            <line x1="1" y1="1" x2="23" y2="23"/>
                          </svg>
                        ) : (
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                            <circle cx="12" cy="12" r="3"/>
                          </svg>
                        )}
                      </button>
                    </div>
                    
                    {/* Password Strength Indicator */}
                    {formData.newPassword && (
                      <div className="mt-2">
                        <div className="d-flex justify-content-between align-items-center mb-1">
                          <small className="text-muted">Password Strength:</small>
                          <small className={`text-${getPasswordStrengthText(passwordStrength).color} fw-semibold`}>
                            {getPasswordStrengthText(passwordStrength).text}
                          </small>
                        </div>
                        <div className="progress" style={{ height: '4px' }}>
                          <div 
                            className={`progress-bar bg-${getPasswordStrengthText(passwordStrength).color}`}
                            style={{ width: `${(passwordStrength / 5) * 100}%` }}
                          ></div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Confirm Password Field */}
                  <div className="mb-4">
                    <label htmlFor="confirmPassword" className="form-label fw-semibold">Confirm Password</label>
                    <div className="input-group">
                      <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        className={`form-control ${errors.confirmPassword ? 'is-invalid' : formData.confirmPassword && !errors.confirmPassword ? 'is-valid' : ''}`}
                        id="confirmPassword"
                        name="confirmPassword"
                        placeholder="Confirm new password"
                        value={formData.confirmPassword}
                        onChange={handleChange}
                        disabled={isLoading}
                      />
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        disabled={isLoading}
                        style={{ borderRadius: '0 6px 6px 0' }}
                      >
                        {showConfirmPassword ? (
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                            <line x1="1" y1="1" x2="23" y2="23"/>
                          </svg>
                        ) : (
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                            <circle cx="12" cy="12" r="3"/>
                          </svg>
                        )}
                      </button>
                    </div>
                    
                    {errors.confirmPassword && (
                      <div className="invalid-feedback d-block">
                        {errors.confirmPassword}
                      </div>
                    )}
                  </div>

                  <button
                    type="submit"
                    className="btn w-100 btn-primary"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Resetting...
                      </>
                    ) : (
                      'Reset Password'
                    )}
                  </button>
                </form>

                <div className="text-center mt-4">
                  <span className="text-muted">Remember your password? </span>
                  <Link 
                    to="/login" 
                    className="text-decoration-none fw-semibold"
                  >
                    Sign in
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword; 